import { inject, Injectable } from '@angular/core'
import { OpenLayersProjectionService } from './open-layers-projection.service'
import WMTS from 'ol/source/WMTS'
import TileWMS from 'ol/source/TileWMS'
import WMTSTileGrid from 'ol/tilegrid/WMTS'
import { getTopLeft } from 'ol/extent'
import { Circle, Fill, Stroke, Style, Text } from 'ol/style'
import MultiPoint from 'ol/geom/MultiPoint'
import { OLColorEnum, OLColorMap } from '../enums/ol-color.enum'
import { OLIntensityEnum, OLIntensityMap } from '../enums/ol-intensity.enum'
import { Feature } from 'ol'
import { Geometry, MultiPolygon, Polygon } from 'ol/geom'
import { IFilterDef } from './layer-filter.interface'
import { ILayerConfigLayerSldBody } from './layer-config.interface'
import _ from 'lodash'
import RenderFeature from 'ol/render/Feature'

@Injectable({
    providedIn: 'root',
})
export class OpenLayersService {
    private openLayersProjectionService: OpenLayersProjectionService

    // constructor(@Inject(OpenLayersProjectionService) private openLayersProjectionService) {
    // }
    constructor() {
        this.openLayersProjectionService = inject(OpenLayersProjectionService)
    }

    // @TODO: config TYPE, layer TYPE
    // @TODO: returning TYPE
    transformLeafletConfigToOpenLayers(config, visible = true): any {
        let source: WMTS | TileWMS, zIndex: number
        const layer = config.layer
        const name = config.id

        const EPSG5514 = this.openLayersProjectionService.EPSG5514

        if (layer.wmts) {

            let matrixIds = EPSG5514.matrixIds
            let resolutions = EPSG5514.resolutions
            if (layer.maxNativeZoom) {
                matrixIds = matrixIds.slice(0, layer.maxNativeZoom + 1)
                resolutions = resolutions.slice(0, layer.maxNativeZoom + 1)
            }

            source = new WMTS({
                url: layer.url,
                requestEncoding: layer.requestEncoding ?? 'KVP',
                crossOrigin: 'anonymous',
                matrixSet: layer.wmts.tilematrixSet,
                attributions: layer.attribution,
                attributionsCollapsible: false,
                layer: layer.wmts.layer,
                format: layer.wmts.format,
                style: layer.wmts.style,
                tileGrid: new WMTSTileGrid({
                    origin: getTopLeft(EPSG5514.projection.getExtent()),
                    extent: EPSG5514.boundaries,
                    resolutions: resolutions,
                    matrixIds: matrixIds,
                }),
            })
            zIndex = layer.zIndex

        } else if (layer.wms) {
            const options: any = {
                url: layer.url,
                attributions: layer.wms.attribution,
                attributionsCollapsible: false,
                imageSmoothing: false, // so CUZK layers look same as in old Lafelt
                // @TODO: resolve ignored error
                // @ts-ignore
                format: layer.wms.format,
                params: {
                    LAYERS: layer.wms.layers,
                    SRS: EPSG5514.code,
                    VERSION: '1.1.1'
                },
            }

            if (layer.filter) {
                options.params.filter = this.getXmlFilter(layer.filter, true)
            }

            if (layer.sldBody) {
                options.params.SLDBODY = this.getSldBody(layer.sldBody)
            }

            source = new TileWMS(options)
            zIndex = layer.wms.zIndex
        }

        return {name, source, zIndex, visible}
    }

    /**
     * Builds OL style definition from text identifier. Words separated by hyphen.
     * First word is color. Second color is style [strong|semi|border|strongborder].
     * Third and/or fourth word are modifiers which add another style [points|text]
     *
     * @param {string} identifier - Up to 4 words separated by hyphen (-).
     * @param {object} feature - Filled by OL when set as a style function instead of an object.
     * @param {number} resolution - Filled by OL when set as a style function instead of an object.
     */
    getColorStyleFromIdentifier(identifier: string, feature: Feature<any> = null, resolution = null): Style | Style[] {
        // @TODO: Maybe separate to single Helper class
        let color: string, intensity: string, modifier1: string, modifier2: string, fillColor: string, borderColor: string
        let fillOpacity: number, borderOpacity: number
        let basicStyle: Style, pointsStyle: Style

        if (!identifier) {
            identifier = 'blue-semi'
        }

        [color, intensity, modifier1, modifier2] = identifier.split('-')

        fillColor = OLColorMap[color as OLColorEnum] ?? '#FFFFFF'
        fillOpacity = OLIntensityMap[intensity as OLIntensityEnum]?.fillOpacity ?? 1
        borderOpacity = OLIntensityMap[intensity as OLIntensityEnum]?.borderOpacity ?? fillOpacity

        if (color === 'yellow' || color === 'black') {
            borderColor = fillColor
            borderOpacity = 1
        } else if (identifier === 'lpis-brown') {
            borderColor = '#6C3208'
            fillColor = '#EB9454'
            fillOpacity = borderOpacity = 0.48
        } else if (identifier === 'lpis-gray') {
            borderColor = '#000000'
            fillColor = '#A0A0A0'
            fillOpacity = borderOpacity = 0.67
        } else if (identifier === 'white-semi') {
            fillOpacity = 0.45
            borderOpacity = 0.68
            borderColor = fillColor
        } else if (identifier === 'transparent') {
            fillOpacity = borderOpacity = 0
            borderColor = fillColor = '#FFFFFF'
        } else if (identifier === 'green-semi-text') {
            fillOpacity = borderOpacity = 0.53
            borderColor = '#deffdf'
            fillColor = '#2ca42e'
        } else {
            borderColor = '#FFFFFF'
        }

        basicStyle = new Style({
            fill: new Fill({
                color: this._hexOpaToRgba(fillColor, fillOpacity),
            }),
            stroke: new Stroke({
                color: this._hexOpaToRgba(borderColor, borderOpacity),
                width: 1
            }),
        })

        if (modifier1 === 'points' || modifier2 === 'points') {
            pointsStyle = new Style({
                image: new Circle({
                    radius: resolution > 0.9 ? 1.5 : 2.5,
                    fill: new Fill({color: borderColor}),
                }),
                geometry: function (feature): MultiPoint {
                    // z bodů našeho polygonu se vytvoří geometrie pro puntíky, jen pro obvod, nikoliv pro díry
                    let geometry: Geometry | RenderFeature = feature.getGeometry()
                    let coords = []
                    // @TODO: Je možné, že zde bude jiný typ než MultiPolygon?
                    if (geometry.getType() === 'MultiPolygon') {
                        coords = (geometry as MultiPolygon).getCoordinates()[0]
                    } else if (geometry.getType() === 'Polygon') {
                        coords = (geometry as Polygon).getCoordinates()
                    }

                    return new MultiPoint(coords.flat())
                },
            })
        }

        if (modifier1 === 'text' || modifier2 === 'text') {

            let fontSize = '12px'
            if (feature.get('geometryOrder') !== undefined) {
                fontSize = '16px'
            }

            let textStyle = new Text({
                text: this._buildFeatureText(feature),
                font: 'normal ' + fontSize + ' sans-serif',
                textAlign: 'center',
                textBaseline: 'middle',
                fill: new Fill({
                    color: borderColor,
                }),
                stroke: feature.get('geometryOrder') !== undefined ? new Stroke({color: 'rgba(0, 0, 0, .1)', width: 4}) : null,
                placement: 'point',
                rotateWithView: true,
                overflow: true,
            })
            basicStyle.setText(textStyle)
        }

        return pointsStyle ? [basicStyle, pointsStyle] : basicStyle
    }

    getCqlFilterFromFilter(filterDef: IFilterDef): string {
        let filter = ''

        if (filterDef?.type) {
            switch (filterDef.type) {
                case 'Or':
                    filter += this.buildCQLOrAndFilter('OR', filterDef)
                    break
                case 'And':
                    filter += this.buildCQLOrAndFilter('AND', filterDef)
                    break
                case 'Not':
                    filter += 'NOT (' + this.getCqlFilterFromFilter(filterDef.arg) + ')'
                    break
                case 'PropertyIsEqualTo':
                    filter += filterDef.propertyName + '=\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsGreaterThan':
                    filter += filterDef.propertyName + '>\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsGreaterThanOrEqualTo':
                    filter += filterDef.propertyName + '>=\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsLessThan':
                    filter += filterDef.propertyName + '<\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsLessThanOrEqualTo':
                    filter += filterDef.propertyName + '<=\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsLike':
                    filter += filterDef.propertyName + ' LIKE \'' + filterDef.literal + '\''
                    break
                case 'PropertyIsNotEqualTo':
                    filter += filterDef.propertyName + '<>\'' + filterDef.literal + '\''
                    break
                case 'PropertyIsNull':
                    filter += filterDef.propertyName + ' IS NULL'
                    break
                default:
                    new Error('Unsupported filter type: ' + filterDef.type)
            }
        }

        return filter
    }

    getXmlFilter(filterDef: IFilterDef, toplevel: boolean): string {
        let filter = ''

        if (toplevel) {
            filter += '<Filter xmlns:gml="http://www.opengis.net/gml">'
        }

        if (filterDef?.type) {
            switch (filterDef.type) {
                case 'Or':
                    filter += this.buildXMLOrAndFilter('Or', filterDef)
                    break
                case 'And':
                    filter += this.buildXMLOrAndFilter('And', filterDef)
                    break
                case 'Not':
                    filter += `<Not>${this.getXmlFilter(filterDef.arg, false)}</Not>`
                    break
                case 'PropertyIsEqualTo':
                case 'PropertyIsGreaterThan':
                case 'PropertyIsGreaterThanOrEqualTo':
                case 'PropertyIsLessThan':
                case 'PropertyIsLessThanOrEqualTo':
                case 'PropertyIsNotEqualTo':
                    filter += '<' + filterDef.type + '>'
                    filter += `<PropertyName>${filterDef.propertyName}</PropertyName>`
                    filter += `<Literal>${filterDef.literal}</Literal>`
                    filter += '</' + filterDef.type + '>'
                    break
                case 'PropertyIsLike':
                    filter += '<PropertyIsLike wildCard="*" matchCase="false" singleChar="." escapeChar="!">'
                    filter += `<PropertyName>${filterDef.propertyName}</PropertyName>`
                    filter += `<Literal>${filterDef.literal}</Literal>`
                    filter += '</PropertyIsLike>'
                    break
                case 'PropertyIsNull':
                    filter += `<PropertyIsNull><PropertyName>${filterDef.propertyName}</PropertyName></PropertyIsNull>`
                    break
                case 'BBOX':
                    filter += '<BBOX>'
                    filter += `<PropertyName>${filterDef.propertyName}</PropertyName>`
                    filter += '<gml:Box><gml:coordinates>'
                    filter += filterDef.coordinates
                    filter += '</gml:coordinates></gml:Box>'
                    filter += '</BBOX>'
                    break
                case 'XML':
                    filter += filterDef.value
                    break
                default:
                    new Error('Unsupported filter type: ' + filterDef.type)
            }
        }

        if (toplevel) {
            filter += '</Filter>'
        }

        return filter
    }

    getSldBody(sldBody: ILayerConfigLayerSldBody): string {
        let result = '<sld:StyledLayerDescriptor xmlns:sld="http://www.opengis.net/sld" version="1.1.0" '
            + 'xmlns="http://www.opengis.net/ogc" '
            + 'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" '
            + 'xmlns:xlink="http://www.w3.org/1999/xlink" '
            + 'xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.1.0/StyledLayerDescriptor.xsd" '
            + 'xmlns:se="http://www.opengis.net/se">'

        result += '<sld:NamedLayer>'

        if (sldBody.name) {
            result += '<se:Name>' + sldBody.name + '</se:Name>'
        }

        result += '<sld:UserStyle><se:FeatureTypeStyle>'

        if (sldBody.rules && _.isArray(sldBody.rules)) {
            _.forEach(sldBody.rules, (rule) => {
                result += '<se:Rule>'

                if (rule.name) {
                    result += '<se:Name>' + rule.name + '</se:Name>'
                }

                // TODO - not valid to XSD
                /*if (rule.maxScaleDenominator) {
                    result += '<se:MaxScaleDenominator>' + rule.maxScaleDenominator + '</se:MaxScaleDenominator>';
                }

                if (rule.minScaleDenominator) {
                    result += '<se:MinScaleDenominator>' + rule.minScaleDenominator + '</se:MinScaleDenominator>';
                }*/

                if (rule.polygonSymbolizer) {
                    result += '<se:PolygonSymbolizer>'

                    if (rule.polygonSymbolizer.fill && rule.polygonSymbolizer.fill.svgParameter) {
                        result += '<se:Fill>'
                        _.forIn(rule.polygonSymbolizer.fill.svgParameter, (value, key) => {
                            result += '<se:SvgParameter name="' + key + '">' + value + '</se:SvgParameter>'
                        })
                        result += '</se:Fill>'
                    }

                    if (rule.polygonSymbolizer.stroke && rule.polygonSymbolizer.stroke.svgParameter) {
                        result += '<se:Stroke>'
                        _.forIn(rule.polygonSymbolizer.stroke.svgParameter, (value, key) => {
                            result += '<se:SvgParameter name="' + key + '">' + value + '</se:SvgParameter>'
                        })
                        result += '</se:Stroke>'
                    }

                    result += '</se:PolygonSymbolizer>'
                }

                if (rule.textSymbolizer) {
                    result += '<se:TextSymbolizer>'

                    if (rule.textSymbolizer.label) {
                        // PropertyName is http://www.opengis.net/ogc namespace
                        result += '<se:Label><PropertyName>' + rule.textSymbolizer.label + '</PropertyName></se:Label>'
                    }

                    if (rule.textSymbolizer.font && rule.textSymbolizer.font.svgParameter) {
                        result += '<se:Font>'
                        _.forIn(rule.textSymbolizer.font.svgParameter, (value, key) => {
                            result += '<se:SvgParameter name="' + key + '">' + value + '</se:SvgParameter>'
                        })
                        result += '</se:Font>'
                    }

                    if (rule.textSymbolizer.labelPlacement
                        && rule.textSymbolizer.labelPlacement.pointPlacement
                        && rule.textSymbolizer.labelPlacement.pointPlacement.anchorPoint
                        && rule.textSymbolizer.labelPlacement.pointPlacement.anchorPoint.anchorPointX
                        && rule.textSymbolizer.labelPlacement.pointPlacement.anchorPoint.anchorPointY
                    ) {
                        result += '<se:LabelPlacement><se:PointPlacement><se:AnchorPoint>'
                        result += '<se:AnchorPointX><Literal>'
                            + rule.textSymbolizer.labelPlacement.pointPlacement.anchorPoint.anchorPointX
                            + '</Literal></se:AnchorPointX>'
                        result += '<se:AnchorPointY><Literal>'
                            + rule.textSymbolizer.labelPlacement.pointPlacement.anchorPoint.anchorPointY
                            + '</Literal></se:AnchorPointY>'
                        result += '</se:AnchorPoint></se:PointPlacement></se:LabelPlacement>'
                    }

                    if (rule.textSymbolizer.halo) {
                        result += '<se:Halo>'

                        if (rule.textSymbolizer.halo.radius) {
                            result += '<se:Radius>' + rule.textSymbolizer.halo.radius + '</se:Radius>'
                        }

                        if (rule.textSymbolizer.halo.fill && rule.textSymbolizer.halo.fill.svgParameter) {
                            result += '<se:Fill>'
                            _.forIn(rule.textSymbolizer.halo.fill.svgParameter, (value, key) => {
                                result += '<se:SvgParameter name="' + key + '">' + value + '</se:SvgParameter>'
                            })
                            result += '</se:Fill>'
                        }

                        result += '</se:Halo>'
                    }

                    if (rule.textSymbolizer.fill && rule.textSymbolizer.fill.svgParameter) {
                        result += '<se:Fill>'
                        _.forIn(rule.textSymbolizer.fill.svgParameter, (value, key) => {
                            result += '<se:SvgParameter name="' + key + '">' + value + '</se:SvgParameter>'
                        })
                        result += '</se:Fill>'
                    }

                    result += '</se:TextSymbolizer>'
                }

                result += '</se:Rule>'
            })
        }

        result += '</se:FeatureTypeStyle></sld:UserStyle>'
        result += '</sld:NamedLayer>'
        result += '</sld:StyledLayerDescriptor>'

        return result
    }

    //////////////////////////////////////////////////////////////////////////


    private buildCQLOrAndFilter(filterType: 'OR' | 'AND', filterDef: IFilterDef): string {
        let filter = ''
        if (filterDef.args && Array.isArray(filterDef.args)) {
            const result = []
            filterDef.args.forEach(value => {
                result.push(this.getCqlFilterFromFilter(value))
            })
            filter += result.join(' ' + filterType + ' ')
        }
        return filter
    }

    private buildXMLOrAndFilter(filterType: 'Or' | 'And', filterDef: IFilterDef): string {
        let filter = ''
        filter += filterType === 'Or' ? '<Or>' : '<And>'
        if (filterDef.args && Array.isArray(filterDef.args)) {
            filterDef.args.forEach(value => {
                filter += this.getXmlFilter(value, false)
            })
        }
        filter += filterType === 'Or' ? '</Or>' : '</And>'
        return filter
    }

    private _hexOpaToRgba(hex: string, opa = 1): string {
        let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        if (result?.length - 1 === 3) {
            return 'rgba(' + parseInt(result[1], 16) + ', ' + parseInt(result[2], 16) + ', ' + parseInt(result[3], 16) + ', ' + opa + ')'
        }
    }

    private _buildFeatureText(feature: Feature<Geometry>): string { // second parameter "resolution" currently unused
        let lpis = feature.get('zkodfb')
        let parcis = feature.get('parcis')
        let order = feature.get('geometryOrder')

        if (lpis && !parcis) {
            return lpis.toString()
        }
        if (parcis && !lpis) {
            return parcis.toString()
        }
        if (order !== undefined) {
            return order.toString()
        }
    }
}